import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

// Notecard single-card tiered pricing
const NOTECARD_SINGLE_TIERS = [
  { min: 10, price: 4 },
  { min: 6,  price: 5 },
  { min: 1,  price: 6 },
];

export function getNotecardSinglePrice(totalSingleQty) {
  const tier = NOTECARD_SINGLE_TIERS.find(t => totalSingleQty >= t.min);
  return tier ? tier.price : 6;
}

// Calendar tiered pricing: 1 @ $28, 2 @ $25 ea, 3+ @ $22 ea
const CALENDAR_TIERS = [
  { min: 3, price: 22 },
  { min: 2, price: 25 },
  { min: 1, price: 28 },
];

export function getCalendarUnitPrice(totalCalendarQty) {
  const tier = CALENDAR_TIERS.find(t => totalCalendarQty >= t.min);
  return tier ? tier.price : 28;
}

function getGeneralShippingCost(subtotal) {
  if (subtotal >= 75.01) return 0;
  if (subtotal >= 50.01) return 9.95;
  return 8.95;
}

function getNotecardShippingCost(subtotal) {
  if (subtotal >= 27.00) return 0;
  if (subtotal >= 18.01) return 4.00;
  if (subtotal >= 6.01) return 3.00;
  return 2.00;
}

function getDrinkwareShippingCost(subtotal) {
  if (subtotal >= 30.00) return 0;
  return 8.00;
}

// Calendar tiered pricing means a 3+ calendar order is always $66.00+ and a
// 1-2 order is always $50.00 or less, so "free at $66+" cleanly maps to
// "free at 3+ calendars" with no overlap — see calculate_calendar_shipping
// in backend/utils/helpers.py.
function getCalendarShippingCost(subtotal) {
  if (subtotal >= 66.00) return 0;
  return getGeneralShippingCost(subtotal);
}

// What shop-only-cart discount, if any, applies — used to pick the right
// shipping schedule and to show the right "free over $X" threshold.
export function getCartShippingProfile(items) {
  const shopsInCart = new Set(items.map((i) => i.shop));
  if (shopsInCart.size === 1 && shopsInCart.has('notecards')) {
    return { shop: 'notecards', threshold: 27.00, calc: getNotecardShippingCost };
  }
  if (shopsInCart.size === 1 && shopsInCart.has('muggs')) {
    return { shop: 'muggs', threshold: 30.00, calc: getDrinkwareShippingCost };
  }
  if (shopsInCart.size === 1 && shopsInCart.has('calendar')) {
    return { shop: 'calendar', threshold: 66.00, calc: getCalendarShippingCost };
  }
  return { shop: 'general', threshold: 75.01, calc: getGeneralShippingCost };
}

// Mirrors the backend /cart/checkout shipping logic — whole cart ships as
// one package, so shipping is one flat calculation on the combined
// subtotal, except a single-shop cart whose shop has its own lower-cost
// packing profile (notecards, muggs) — see getCartShippingProfile.
export function getCartShippingCost(items, subtotal) {
  return getCartShippingProfile(items).calc(subtotal);
}

function getEffectiveUnitPrice(item, notecardSinglePrice, calendarUnitPrice) {
  if (item.shop === 'notecards' && item.variant === 'single') {
    return notecardSinglePrice;
  }
  if (item.shop === 'calendar') {
    return calendarUnitPrice;
  }
  return item.price;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem('obxbb_cart');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('obxbb_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (newItem) => {
    const key = `${newItem.shop}-${newItem.product_id}-${newItem.variant || ''}${newItem.color ? `-${newItem.color}` : ''}`;
    setItems(prev => {
      const existing = prev.find(i => i.cartItemId === key);
      if (existing) {
        return prev.map(i =>
          i.cartItemId === key
            ? { ...i, quantity: i.quantity + (newItem.quantity || 1) }
            : i
        );
      }
      return [...prev, { ...newItem, cartItemId: key, quantity: newItem.quantity || 1 }];
    });
    setIsOpen(true);
  };

  const removeItem = (cartItemId) => {
    setItems(prev => prev.filter(i => i.cartItemId !== cartItemId));
  };

  const updateQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      removeItem(cartItemId);
      return;
    }
    setItems(prev =>
      prev.map(i => i.cartItemId === cartItemId ? { ...i, quantity } : i)
    );
  };

  const clearCart = () => setItems([]);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  const totalNotecardSingles = items
    .filter(i => i.shop === 'notecards' && i.variant === 'single')
    .reduce((sum, i) => sum + i.quantity, 0);
  const notecardSinglePrice = getNotecardSinglePrice(totalNotecardSingles);

  const totalCalendarQty = items
    .filter(i => i.shop === 'calendar')
    .reduce((sum, i) => sum + i.quantity, 0);
  const calendarUnitPrice = getCalendarUnitPrice(totalCalendarQty);

  const subtotal = items.reduce((sum, i) =>
    sum + getEffectiveUnitPrice(i, notecardSinglePrice, calendarUnitPrice) * i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      itemCount,
      subtotal,
      notecardSinglePrice,
      calendarUnitPrice,
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false)
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
